/* Nova theme JS — cart, product forms, and micro-interactions. */
(() => {
  'use strict';

  /* ---------------------------------------------------------------- */
  /* Reveal-on-scroll                                                  */
  /* ---------------------------------------------------------------- */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  const observeReveals = (root = document) => {
    root.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => revealObserver.observe(el));
  };

  observeReveals();
  document.addEventListener('shopify:section:load', (e) => observeReveals(e.target));

  /* ---------------------------------------------------------------- */
  /* Header scroll state                                               */
  /* ---------------------------------------------------------------- */
  const header = document.querySelector('.site-header');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------------------------------------------------------------- */
  /* Cart helpers                                                      */
  /* ---------------------------------------------------------------- */
  const CART_DRAWER_SECTION = 'cart-drawer';

  const refreshCart = async ({ open = false } = {}) => {
    const [sectionRes, cartRes] = await Promise.all([
      fetch(`${window.Shopify?.routes?.root || '/'}?sections=${CART_DRAWER_SECTION}`),
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`),
    ]);
    const sections = await sectionRes.json();
    const cart = await cartRes.json();

    const drawer = document.querySelector('cart-drawer');
    if (drawer && sections[CART_DRAWER_SECTION]) {
      const doc = new DOMParser().parseFromString(sections[CART_DRAWER_SECTION], 'text/html');
      const fresh = doc.querySelector('.cart-drawer__inner');
      const current = drawer.querySelector('.cart-drawer__inner');
      if (fresh && current) current.replaceWith(fresh);
    }

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.toggleAttribute('hidden', cart.item_count === 0);
    });

    if (open && drawer) drawer.open();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  };

  window.NovaCart = { refresh: refreshCart };

  /* ---------------------------------------------------------------- */
  /* <cart-drawer>                                                     */
  /* ---------------------------------------------------------------- */
  customElements.define(
    'cart-drawer',
    class extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');

        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-drawer-close]')) this.close();
          // Click on the backdrop (the dialog itself, not its children).
          if (e.target === this.dialog) this.close();
        });

        this.addEventListener('change', (e) => {
          const input = e.target.closest('[data-line-quantity]');
          if (!input) return;
          const min = parseInt(input.min, 10) || 0;
          const maxAttr = input.getAttribute('max');
          const max = maxAttr !== null && maxAttr !== '' ? parseInt(maxAttr, 10) : Infinity;
          let qty = parseInt(input.value, 10);
          if (Number.isNaN(qty)) qty = min;
          qty = Math.max(min, qty);
          if (Number.isFinite(max)) qty = Math.min(max, qty);
          input.value = qty;
          this.changeLine(input.dataset.lineKey, qty);
        });

        this.addEventListener('click', (e) => {
          const removeBtn = e.target.closest('[data-line-remove]');
          if (removeBtn) {
            e.preventDefault();
            this.changeLine(removeBtn.dataset.lineKey, 0);
          }
        });

        document.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            this.open();
          });
        });
      }

      open() {
        if (!this.dialog.open) this.dialog.showModal();
      }

      close() {
        this.dialog.close();
      }

      async changeLine(key, quantity) {
        this.classList.add('is-busy');
        try {
          await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity }),
          });
          await refreshCart();
        } finally {
          this.classList.remove('is-busy');
        }
      }
    }
  );

  /* ---------------------------------------------------------------- */
  /* <product-form> — AJAX add to cart                                 */
  /* ---------------------------------------------------------------- */
  customElements.define(
    'product-form',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitBtn = this.querySelector('[type="submit"]');
        this.errorEl = this.querySelector('[data-form-error]');
        this.qtyInput = this.querySelector('[data-qty-input], input[name="quantity"]');
        if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
      }

      async onSubmit(e) {
        e.preventDefault();
        if (this.submitBtn?.hasAttribute('disabled')) return;

        const qty = parseInt(this.qtyInput?.value, 10) || 0;
        const max = this.qtyInput?.max !== '' ? parseInt(this.qtyInput.max, 10) : Infinity;
        if (qty < 1) return;
        if (Number.isFinite(max) && qty > max) {
          if (this.errorEl) {
            this.errorEl.textContent =
              max <= 0
                ? 'Only the available stock is already in your cart.'
                : `Only ${max} left in stock.`;
            this.errorEl.hidden = false;
          }
          if (this.qtyInput && Number.isFinite(max)) this.qtyInput.value = Math.max(0, max);
          return;
        }

        const originalLabel = this.submitBtn?.querySelector('[data-btn-label]');
        this.submitBtn?.setAttribute('aria-busy', 'true');
        if (originalLabel) originalLabel.dataset.original = originalLabel.textContent;
        if (originalLabel) originalLabel.textContent = originalLabel.dataset.addingText || 'Adding…';
        if (this.errorEl) this.errorEl.hidden = true;

        try {
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(this.form),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
          await refreshCart({ open: true });
          // After add, remaining stock for this variant drops — refresh max from cart.
          document.dispatchEvent(new CustomEvent('cart:stock-refresh'));
        } catch (err) {
          if (this.errorEl) {
            this.errorEl.textContent = err.message;
            this.errorEl.hidden = false;
          }
        } finally {
          this.submitBtn?.removeAttribute('aria-busy');
          if (originalLabel) originalLabel.textContent = originalLabel.dataset.original;
        }
      }
    }
  );

  /* ---------------------------------------------------------------- */
  /* <quantity-input>                                                  */
  /* ---------------------------------------------------------------- */
  customElements.define(
    'quantity-input',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('button');
          if (!btn || !this.input) return;
          e.preventDefault();
          const step = btn.dataset.action === 'increment' ? 1 : -1;
          const min = parseInt(this.input.min, 10) || 0;
          const maxAttr = this.input.getAttribute('max');
          const max = maxAttr !== null && maxAttr !== '' ? parseInt(maxAttr, 10) : Infinity;
          let next = (parseInt(this.input.value, 10) || 0) + step;
          next = Math.max(min, next);
          if (Number.isFinite(max)) next = Math.min(max, next);
          if (this.input.value !== String(next)) {
            this.input.value = next;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        this.input?.addEventListener('change', () => {
          const min = parseInt(this.input.min, 10) || 0;
          const maxAttr = this.input.getAttribute('max');
          const max = maxAttr !== null && maxAttr !== '' ? parseInt(maxAttr, 10) : Infinity;
          let val = parseInt(this.input.value, 10);
          if (Number.isNaN(val)) val = min;
          val = Math.max(min, val);
          if (Number.isFinite(max)) val = Math.min(max, val);
          this.input.value = val;
        });
      }
    }
  );

  /* ---------------------------------------------------------------- */
  /* <variant-picker>                                                  */
  /* ---------------------------------------------------------------- */
  customElements.define(
    'variant-picker',
    class extends HTMLElement {
      connectedCallback() {
        const dataEl =
          this.querySelector('[data-variants-json]') ||
          this.closest('product-form')?.querySelector('[data-variants-json]') ||
          this.closest('product-info')?.querySelector('[data-variants-json]');
        if (!dataEl) return;
        this.variants = JSON.parse(dataEl.textContent);
        this.addEventListener('change', () => this.onOptionChange());
      }

      get selectedOptions() {
        return [...this.querySelectorAll('fieldset')].map(
          (set) => set.querySelector('input:checked')?.value
        );
      }

      onOptionChange() {
        const selected = this.selectedOptions;
        const variant = this.variants.find((v) =>
          v.options.every((opt, i) => opt === selected[i])
        );
        this.dispatchEvent(
          new CustomEvent('variant:change', { bubbles: true, detail: { variant } })
        );
      }
    }
  );

  /* ---------------------------------------------------------------- */
  /* <product-info> — reacts to variant changes on the PDP             */
  /* ---------------------------------------------------------------- */
  customElements.define(
    'product-info',
    class extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.page = this.closest('.product-page');
        const dataEl = this.querySelector('[data-variants-json]');
        this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
        this.addEventListener('variant:change', (e) => this.onVariantChange(e.detail.variant));
        this.initGallery();
        this.initStickyBuy();
        this._onStockRefresh = () => this.refreshQuantityMax();
        document.addEventListener('cart:stock-refresh', this._onStockRefresh);
        document.addEventListener('cart:updated', this._onStockRefresh);
      }

      disconnectedCallback() {
        document.removeEventListener('cart:stock-refresh', this._onStockRefresh);
        document.removeEventListener('cart:updated', this._onStockRefresh);
      }

      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
      }

      getSelectedVariant() {
        const id = Number(this.querySelector('input[name="id"]')?.value);
        return this.variants.find((v) => v.id === id) || this.variants[0];
      }

      async cartQuantityForVariant(variantId) {
        try {
          const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) =>
            r.json()
          );
          return (cart.items || [])
            .filter((item) => item.variant_id === variantId)
            .reduce((sum, item) => sum + item.quantity, 0);
        } catch {
          return 0;
        }
      }

      inventoryCap(variant) {
        if (!variant?.inventory_management) return null;
        const qty = variant.inventory_quantity;
        // Storefront often omits qty, or shows 0 when stock is on a non-online location
        // while the variant remains available (CONTINUE). Don't block the UI in that case.
        if (qty == null || Number.isNaN(Number(qty))) return null;
        if (Number(qty) <= 0 && variant.available) return null;
        return Math.max(0, Number(qty));
      }

      async refreshQuantityMax(variant = this.getSelectedVariant()) {
        const qtyInput = this.querySelector('[data-qty-input], input[name="quantity"]');
        const submitBtn = this.querySelector('[type="submit"]');
        if (!qtyInput || !variant) return;

        const cap = this.inventoryCap(variant);
        if (cap === null) {
          qtyInput.removeAttribute('max');
          qtyInput.min = variant.available ? '1' : '0';
          if (variant.available) {
            if ((parseInt(qtyInput.value, 10) || 0) < 1) qtyInput.value = '1';
            submitBtn?.removeAttribute('disabled');
          } else {
            qtyInput.value = '0';
            submitBtn?.setAttribute('disabled', '');
          }
          return;
        }

        const inCart = await this.cartQuantityForVariant(variant.id);
        const remaining = Math.max(0, cap - inCart);
        qtyInput.max = String(remaining);
        qtyInput.min = remaining > 0 ? '1' : '0';

        let val = parseInt(qtyInput.value, 10) || 0;
        if (remaining <= 0) {
          qtyInput.value = '0';
          submitBtn?.setAttribute('disabled', '');
        } else {
          if (val < 1) val = 1;
          if (val > remaining) val = remaining;
          qtyInput.value = String(val);
          if (variant.available) submitBtn?.removeAttribute('disabled');
        }
      }

      initGallery() {
        const mainImg = this.page?.querySelector('.product-page__media--main img');
        this.page?.querySelectorAll('[data-thumb]').forEach((thumb) => {
          thumb.addEventListener('click', () => {
            if (!mainImg || !thumb.dataset.mediaSrc) return;
            mainImg.src = thumb.dataset.mediaSrc;
            if (thumb.dataset.mediaSrcset) mainImg.srcset = thumb.dataset.mediaSrcset;
            if (thumb.dataset.mediaAlt != null) mainImg.alt = thumb.dataset.mediaAlt;
            this.page.querySelectorAll('[data-thumb]').forEach((t) => t.classList.remove('is-active'));
            thumb.classList.add('is-active');
          });
        });
      }

      initStickyBuy() {
        const sticky = this.page?.querySelector('[data-sticky-buy]');
        const buyRow = this.page?.querySelector('[data-buy-row]');
        const stickyAdd = sticky?.querySelector('[data-sticky-add]');
        const mainAdd = this.querySelector('[data-main-add]');
        if (!sticky || !buyRow || !stickyAdd || !mainAdd) return;

        sticky.hidden = false;

        const observer = new IntersectionObserver(
          ([entry]) => {
            sticky.classList.toggle('is-visible', !entry.isIntersecting);
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0 }
        );
        observer.observe(buyRow);

        stickyAdd.addEventListener('click', () => {
          if (mainAdd.disabled) return;
          mainAdd.click();
        });
      }

      syncSticky(variant) {
        const sticky = this.page?.querySelector('[data-sticky-buy]');
        if (!sticky) return;
        const price = sticky.querySelector('[data-sticky-price]');
        const btn = sticky.querySelector('[data-sticky-add]');
        const label = sticky.querySelector('[data-sticky-label]');
        const mainLabel = this.querySelector('[data-btn-label]');

        if (price && variant) price.textContent = this.formatMoney(variant.price);

        if (!variant) {
          btn?.setAttribute('disabled', '');
          if (label) label.textContent = mainLabel?.dataset.unavailableText || 'Unavailable';
          return;
        }

        if (variant.available) {
          btn?.removeAttribute('disabled');
          if (label) label.textContent = mainLabel?.dataset.addText || 'Add to cart';
        } else {
          btn?.setAttribute('disabled', '');
          if (label) label.textContent = mainLabel?.dataset.soldOutText || 'Sold out';
        }
      }

      onVariantChange(variant) {
        const idInput = this.querySelector('input[name="id"]');
        const submitBtn = this.querySelector('[type="submit"]');
        const btnLabel = submitBtn?.querySelector('[data-btn-label]');
        const priceEl = this.querySelector('[data-price]');
        const compareEl = this.querySelector('[data-compare-price]');

        if (!variant) {
          submitBtn?.setAttribute('disabled', '');
          if (btnLabel) btnLabel.textContent = btnLabel.dataset.unavailableText || 'Unavailable';
          this.syncSticky(null);
          return;
        }

        if (idInput) idInput.value = variant.id;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
        if (compareEl) {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compareEl.hidden = !onSale;
          if (onSale) compareEl.textContent = this.formatMoney(variant.compare_at_price);
          priceEl?.closest('[data-price-wrapper]')?.classList.toggle('price--sale', !!onSale);
        }

        if (variant.available) {
          submitBtn?.removeAttribute('disabled');
          if (btnLabel) btnLabel.textContent = btnLabel.dataset.addText || 'Add to cart';
        } else {
          submitBtn?.setAttribute('disabled', '');
          if (btnLabel) btnLabel.textContent = btnLabel.dataset.soldOutText || 'Sold out';
        }

        this.syncSticky(variant);
        this.refreshQuantityMax(variant);

        if (variant.featured_media?.id) {
          const thumb = this.page?.querySelector(`[data-thumb][data-media-id="${variant.featured_media.id}"]`);
          thumb?.click();
        }
      }
    }
  );

  /* ---------------------------------------------------------------- */
  /* Quick add (single-variant products from cards)                    */
  /* ---------------------------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(btn.dataset.variantId), quantity: 1 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(data.description || data.message || 'Not enough stock to add this item.');
        return;
      }
      await refreshCart({ open: true });
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });
})();
